/**
 * TableRealtimeSync - Sincronización realtime genérica para tablas empresa-scoped
 *
 * Generaliza el patrón "notify + catch-up" que usaba `animales` a cualquier
 * cantidad de tablas, sobre un ÚNICO canal por empresa (`empresa:{id}`).
 *
 * Modelo:
 *  - Los triggers SQL emiten `broadcast` al canal `empresa:{id}` con payload
 *    `{ table, op, empresa_id, ids[] }` (sin datos, solo aviso).
 *  - Esta clase se suscribe una sola vez al canal y, según `payload.table`,
 *    agenda un catch-up debounced para esa tabla.
 *  - El catch-up trae los datos reales por `updated_at` (`.gt('updated_at')`,
 *    `.eq('empresa_id')`) y los aplica con `bulkPutSilent` / `bulkDeleteSilent`.
 *
 * Las tablas se registran con `register(cfg)`. Reemplaza a `AnimalRealtimeSync`
 * (animales es ahora un caso más de esta clase).
 *
 * Ver: docs/REALTIME_GENERICO_TODAS_TABLAS.md
 */

import { SupabaseClient, RealtimeChannel } from '@supabase/supabase-js';
import { getRepositories } from '../services/storage';
import {
  pullTableIncremental,
  PullTableConfig,
} from './syncPull';

/**
 * Estados de la sincronización realtime
 */
export enum RealtimeSyncState {
  IDLE = 'idle',
  INITIAL_SYNC = 'initial_sync',
  LISTENING = 'listening',
  DISCONNECTED = 'disconnected',
  ERROR = 'error'
}

/**
 * Callback de notificación: se invoca (sin args) tras un catch-up de la tabla.
 * La UI normalmente prefiere `localRepositoryEvents` (emitido por bulkPutSilent);
 * este listener queda como API de respaldo.
 */
export type TableUpdateCallback = () => void;

/**
 * Configuración de una tabla para el catch-up realtime.
 *
 * Alias de `PullTableConfig` (en `./syncPull`): la lógica de PULL incremental
 * vive ahora en ese módulo puro compartido, así realtime y SyncManager usan la
 * misma función de catch-up. Ver `syncPull.ts` para el detalle de los campos.
 */
export type RealtimeTableConfig = PullTableConfig;

export interface TableRealtimeSyncOptions {
  reconnectDelay?: number;
  retryDelay?: number;
  maxRetries?: number;
  /** Debounce del catch-up ante bursts de pings (ms). */
  catchUpDebounce?: number;
}

/**
 * Repositorio mínimo que esta clase usa. `getRepositories()` ya devuelve
 * objetos que cumplen esta forma (vía BaseRepository / adapters).
 */
interface RealtimeRepository {
  get(id: string): Promise<any>;
  bulkPutSilent(items: any[]): Promise<void>;
  bulkDeleteSilent(ids: string[]): Promise<void>;
  getLastByDate(dateColumn?: string): Promise<any>;
  getLastByDateForEmpresa?(empresaId: string, dateColumn?: string): Promise<any>;
}

export class TableRealtimeSync {
  private supabase: SupabaseClient;
  private channel: RealtimeChannel | null = null;
  private empresaId: string | null = null;
  private state: RealtimeSyncState = RealtimeSyncState.IDLE;
  private isOnline: boolean = true;

  /** Tablas registradas, indexadas por nombre de tabla Supabase. */
  private configs: Map<string, RealtimeTableConfig> = new Map();
  /** Listeners por tabla (notificación post-catch-up). */
  private listeners: Map<string, Set<TableUpdateCallback>> = new Map();

  // Reconexión / concurrencia
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private retryCount: number = 0;
  private isReconnecting: boolean = false;
  private subscribePromise: Promise<void> | null = null;

  // Catch-up debounced
  private catchUpTimer: ReturnType<typeof setTimeout> | null = null;
  private isCatchingUp: boolean = false;
  /** Tablas con cambios pendientes de catch-up. */
  private pendingTables: Set<string> = new Set();

  private options: Required<TableRealtimeSyncOptions>;

  constructor(supabase: SupabaseClient, options: TableRealtimeSyncOptions = {}) {
    this.supabase = supabase;
    this.options = {
      reconnectDelay: options.reconnectDelay ?? 1000,
      retryDelay: options.retryDelay ?? 5000,
      maxRetries: options.maxRetries ?? 10,
      catchUpDebounce: options.catchUpDebounce ?? 400
    };
  }

  // ==================== Registro de tablas ====================

  /**
   * Registra una tabla para sincronización realtime. Idempotente.
   */
  register(cfg: RealtimeTableConfig): void {
    this.configs.set(cfg.table, cfg);
    if (!this.listeners.has(cfg.table)) {
      this.listeners.set(cfg.table, new Set());
    }
  }

  /**
   * ¿Está registrada esta tabla?
   */
  isRegistered(table: string): boolean {
    return this.configs.has(table);
  }

  // ==================== Ciclo de vida ====================

  /**
   * Inicializa el realtime para una empresa (un canal único para todas las
   * tablas registradas). Idempotente: si ya escucha la misma empresa, no hace nada.
   */
  async initialize(empresaId: string): Promise<void> {
    if (this.empresaId === empresaId && this.state === RealtimeSyncState.LISTENING) {
      console.log('[TableRealtimeSync] Ya inicializado para empresa:', empresaId);
      return;
    }

    console.log(
      '[TableRealtimeSync] Inicializando para empresa:', empresaId,
      '(tablas:', Array.from(this.configs.keys()).join(', ') + ')'
    );
    this.empresaId = empresaId;
    this.state = RealtimeSyncState.INITIAL_SYNC;
    this.retryCount = 0;

    await this.subscribe();
  }

  /**
   * Suscribe al canal `empresa:{id}` (compartido por todas las tablas).
   */
  private async subscribe(): Promise<void> {
    if (!this.empresaId) {
      console.warn('[TableRealtimeSync] No se puede suscribir: empresaId no definido');
      return;
    }

    if (this.state === RealtimeSyncState.LISTENING) {
      console.log('[TableRealtimeSync] Ya está suscrito y escuchando, omitiendo');
      return;
    }

    // Evita levantar un segundo canal mientras uno se está creando.
    if (this.subscribePromise) {
      console.log('[TableRealtimeSync] Suscripción en progreso, esperando...');
      return this.subscribePromise;
    }

    this.subscribePromise = this._doSubscribe().finally(() => {
      this.subscribePromise = null;
    });
    return this.subscribePromise;
  }

  private async _doSubscribe(): Promise<void> {
    if (this.channel) {
      console.log('[TableRealtimeSync] Limpiando canal existente antes de suscribir');
      await this.supabase.removeChannel(this.channel);
      this.channel = null;
    }

    // Canal unificado por empresa: todas las tablas empresa-scoped comparten
    // el mismo canal y se discriminan por `payload.table`.
    const channelName = `empresa:${this.empresaId}`;

    console.log('[TableRealtimeSync] Suscribiendo a canal:', channelName);

    await this.supabase.realtime.setAuth();

    const { data: { session } } = await this.supabase.auth.getSession();
    console.log('[TableRealtimeSync] Supabase session:', {
      hasSession: !!session,
      hasRealtime: !!this.supabase.realtime,
      isConnected: this.supabase.realtime.isConnected(),
      accessToken: session?.access_token ? 'Present' : 'Missing'
    });

    const matches = (msg: any): RealtimeTableConfig | null => {
      const table = msg?.payload?.table;
      if (!table) return null;
      const cfg = this.configs.get(table);
      return cfg ?? null;
    };

    this.channel = this.supabase
      .channel(channelName, { config: { private: true } })
      .on('broadcast', { event: 'INSERT' }, (msg) => { const c = matches(msg); if (c) this.handleTableUpdate(c, msg); })
      .on('broadcast', { event: 'UPDATE' }, (msg) => { const c = matches(msg); if (c) this.handleTableUpdate(c, msg); })
      .on('broadcast', { event: 'DELETE' }, (msg) => { const c = matches(msg); if (c) this.handleTableUpdate(c, msg); })
      .subscribe((status, err) => {
        console.log('[TableRealtimeSync] Estado del canal:', status, err ? `Error: ${JSON.stringify(err)}` : '');

        if (status === 'SUBSCRIBED') {
          this.state = RealtimeSyncState.LISTENING;
          this.retryCount = 0;
          this.isReconnecting = false;
          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
          }
          console.log('[TableRealtimeSync] ✅ Suscrito a cambios realtime');
          // Catch-up de TODAS las tablas registradas para tapar la brecha entre
          // el último sync local y la suscripción.
          this.catchUpAll();
        } else if (status === 'CHANNEL_ERROR') {
          this.state = RealtimeSyncState.ERROR;
          console.error('[TableRealtimeSync] ❌ CHANNEL_ERROR:', {
            error: err, channel: channelName, isPrivate: true, empresaId: this.empresaId
          });
          this.scheduleRetry();
        } else if (status === 'TIMED_OUT') {
          this.state = RealtimeSyncState.ERROR;
          console.warn('[TableRealtimeSync] ⏱️ Timeout en el canal de realtime');
          this.scheduleRetry();
        } else if (status === 'CLOSED') {
          this.state = RealtimeSyncState.DISCONNECTED;
          console.log('[TableRealtimeSync] 🔌 Canal cerrado');
        }
      });
  }

  // ==================== Manejo de eventos ====================

  /**
   * Notificación realtime para una tabla. Modelo "notify + catch-up": el evento
   * no trae datos, solo op + ids. Se agenda un catch-up debounced por tabla.
   * Para DELETE (físico) se actúa de inmediato para snappy UX; los soft-deletes
   * llegan como UPDATE y los resuelve el catch-up.
   */
  private async handleTableUpdate(cfg: RealtimeTableConfig, msg: any): Promise<void> {
    if (!this.isOnline) {
      console.log('[TableRealtimeSync] Ignorando evento (offline):', cfg.table);
      return;
    }

    const op = msg?.event; // INSERT | UPDATE | DELETE
    const ids: string[] = Array.isArray(msg?.payload?.ids)
      ? msg.payload.ids
      : (Array.isArray(msg?.payload?.caravanas) ? msg.payload.caravanas : []); // compat animales

    console.log('[TableRealtimeSync] Notificación:', cfg.table, op, { ids });

    try {
      if (op === 'DELETE') {
        const repo = this.getRepo(cfg);
        if (repo) {
          for (const id of ids) {
            try {
              const existing = await repo.get(id);
              if (existing) await repo.bulkDeleteSilent([id]);
            } catch (e) {
              console.error(`[TableRealtimeSync] Error en DELETE ${cfg.table}:${id}`, e);
            }
          }
        }
      }
      this.scheduleCatchUp(cfg.table);
    } catch (error) {
      console.error('[TableRealtimeSync] Error manejando actualización:', error);
    }
  }

  private getRepo(cfg: RealtimeTableConfig): RealtimeRepository | null {
    try {
      if (!cfg.repoKey) return null; // configs con repo inline (sin factory key)
      const repo = (getRepositories() as any)[cfg.repoKey];
      return (repo ?? null) as RealtimeRepository | null;
    } catch {
      return null;
    }
  }

  // ==================== Catch-up ====================
  // La lógica de PULL incremental vive en el módulo puro `syncPull`
  // (pullTableSince / pullTableIncremental), compartido con SyncManager. Esta
  // clase solo orquesta cuándo correrlo (debounce, isCatchingUp, brechas).

  /**
   * Agenda un catch-up debounced para una o varias tablas. Coalescea bursts de
   * pings en una sola pasada.
   */
  private scheduleCatchUp(table?: string): void {
    if (table) this.pendingTables.add(table);

    if (this.catchUpTimer) clearTimeout(this.catchUpTimer);
    this.catchUpTimer = setTimeout(() => {
      this.catchUpTimer = null;
      void this.runDebouncedCatchUp();
    }, this.options.catchUpDebounce);
  }

  /**
   * Ejecuta el catch-up de las tablas pendientes y, al terminar, notifica a sus
   * listeners (bulkPutSilent es silent: no emite eventos de repositorio).
   */
  private async runDebouncedCatchUp(): Promise<void> {
    const tables = Array.from(this.pendingTables);
    this.pendingTables.clear();
    try {
      await this.catchUpTables(tables);
    } catch (error) {
      console.error('[TableRealtimeSync] Error en catch-up debounced:', error);
    } finally {
      tables.forEach(t => this.notifyListeners(t));
    }
  }

  /** Catch-up de un subconjunto de tablas (las pendientes). */
  private async catchUpTables(tables: string[]): Promise<void> {
    if (this.isCatchingUp || tables.length === 0) return;
    if (!this.empresaId) return;
    this.isCatchingUp = true;
    try {
      for (const table of tables) {
        const cfg = this.configs.get(table);
        if (!cfg) continue;
        await pullTableIncremental(this.supabase, cfg, this.empresaId);
      }
    } finally {
      this.isCatchingUp = false;
    }
  }

  /**
   * Catch-up de TODAS las tablas registradas. Se ejecuta justo después de que
   * la suscripción queda activa, para tapar la brecha mientras la web estuvo
   * cerrada/sin conexión y no perder eventos del periodo.
   */
  private async catchUpAll(): Promise<void> {
    if (this.isCatchingUp) {
      console.log('[TableRealtimeSync] Catch-up ya en progreso, omitiendo catchUpAll');
      return;
    }
    if (!this.empresaId) return;
    const all = Array.from(this.configs.keys());
    if (all.length === 0) return;
    this.isCatchingUp = true;
    try {
      for (const table of all) {
        const cfg = this.configs.get(table)!;
        await pullTableIncremental(this.supabase, cfg, this.empresaId);
      }
    } catch (error) {
      console.error('[TableRealtimeSync] Error en catchUpAll:', error);
    } finally {
      this.isCatchingUp = false;
    }
  }

  // ==================== Reconexión / red ====================

  private scheduleRetry(): void {
    if (this.isReconnecting) {
      console.log('[TableRealtimeSync] Ya hay una reconexión en progreso, omitiendo');
      return;
    }
    // Sin red no se reintenta: la reconexión la dispara onNetworkChange(true).
    // Reintentar offline solo genera un loop infinito de errores.
    if (!this.isOnline) {
      console.log('[TableRealtimeSync] Sin red — reintento postergado hasta reconectar');
      this.state = RealtimeSyncState.DISCONNECTED;
      return;
    }
    if (this.retryCount >= this.options.maxRetries) {
      console.error('[TableRealtimeSync] Máximo de reintentos alcanzado');
      this.state = RealtimeSyncState.ERROR;
      return;
    }
    this.retryCount++;
    const delay = this.options.retryDelay * Math.pow(1.5, this.retryCount - 1);
    console.log(`[TableRealtimeSync] Reintentando en ${delay}ms (${this.retryCount}/${this.options.maxRetries})`);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => { void this.performReconnection(); }, delay);
  }

  onNetworkChange(online: boolean): void {
    console.log('[TableRealtimeSync] Cambio de red:', online ? 'online' : 'offline');
    this.isOnline = online;
    if (!online) {
      this.state = RealtimeSyncState.DISCONNECTED;
      this.isReconnecting = false;
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
    } else {
      this.reconnect();
    }
  }

  /**
   * Garantiza que el canal esté escuchando. Si no lo está, fuerza la reconexión.
   *
   * Caso de uso: al volver a una pestaña que estuvo oculta, el navegador suele
   * haber suspendido/cerrado el WebSocket de realtime por inactividad. La lib
   * de Supabase reconecta el transporte (Phoenix socket) y dispara heartbeats,
   * PERO el manejo de estado acá arriba tiene huecos: el status `CLOSED` no
   * agenda retry, y si se agotaron los `maxRetries` el canal queda en `ERROR`
   * permanente. Sin esto, `isCoveredByRealtime()` devolvería false para siempre
   * y el SyncManager revertiría al PULL periódico de todas las tablas.
   *
   * Idempotente: si ya está LISTENING no hace nada.
   */
  ensureConnected(): void {
    if (this.state === RealtimeSyncState.LISTENING) return;
    if (!this.isOnline) {
      console.log('[TableRealtimeSync] ensureConnected omitido: sin red (reconecta onNetworkChange)');
      return;
    }
    if (!this.empresaId) {
      console.log('[TableRealtimeSync] ensureConnected omitido: sin empresaId');
      return;
    }
    // ERROR terminal: reseteamos el contador de reintentos para que vuelva a
    // tener chances (de lo contrario reconnect()/performReconnection no harían
    // nada útil con retryCount ya en el máximo).
    if (this.state === RealtimeSyncState.ERROR) {
      console.log('[TableRealtimeSync] Estado ERROR — reseteando reintentos para reconectar');
      this.retryCount = 0;
      this.isReconnecting = false;
    }
    console.log('[TableRealtimeSync] Forzando reconexión (ensureConnected), estado:', this.state);
    this.reconnect();
  }

  private reconnect(): void {
    if (this.state === RealtimeSyncState.LISTENING) {
      console.log('[TableRealtimeSync] Ya está suscrito, no reconectar');
      return;
    }
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => { void this.performReconnection(); }, this.options.reconnectDelay);
  }

  private async performReconnection(): Promise<void> {
    if (this.isReconnecting) return;
    if (!this.isOnline) {
      // Se agotó la red entre el schedule y ahora: esperar a onNetworkChange.
      this.state = RealtimeSyncState.DISCONNECTED;
      return;
    }
    if (this.state === RealtimeSyncState.LISTENING) {
      this.retryCount = 0;
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      return;
    }
    this.isReconnecting = true;
    console.log('[TableRealtimeSync] 🔁 Reconectando...');
    try {
      // Al quedar SUBSCRIBED se dispara catchUpAll() (sin brecha).
      await this.subscribe();
      console.log('[TableRealtimeSync] ✅ Reconexión completada');
    } catch (error) {
      console.error('[TableRealtimeSync] ❌ Error en reconexión:', error);
      this.state = RealtimeSyncState.ERROR;
      this.scheduleRetry();
    } finally {
      this.isReconnecting = false;
    }
  }

  // ==================== Listeners / estado ====================

  private notifyListeners(table: string): void {
    const set = this.listeners.get(table);
    if (!set) return;
    set.forEach(cb => {
      try { cb(); } catch (e) { console.error('[TableRealtimeSync] Error en listener:', e); }
    });
  }

  /**
   * Agrega un listener para una tabla. @returns función para removerlo.
   */
  addListener(table: string, callback: TableUpdateCallback): () => void {
    let set = this.listeners.get(table);
    if (!set) {
      set = new Set();
      this.listeners.set(table, set);
    }
    set.add(callback);
    return () => { set!.delete(callback); };
  }

  getState(): RealtimeSyncState {
    return this.state;
  }

  isListening(): boolean {
    return this.state === RealtimeSyncState.LISTENING;
  }

  getEmpresaId(): string | null {
    return this.empresaId;
  }

  /**
   * Desconecta y limpia recursos.
   */
  async disconnect(): Promise<void> {
    console.log('[TableRealtimeSync] Desconectando...');
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.catchUpTimer) {
      clearTimeout(this.catchUpTimer);
      this.catchUpTimer = null;
    }
    this.pendingTables.clear();
    this.subscribePromise = null;

    if (this.channel) {
      await this.supabase.removeChannel(this.channel);
      this.channel = null;
    }

    this.empresaId = null;
    this.state = RealtimeSyncState.IDLE;
    this.retryCount = 0;
    this.isReconnecting = false;
    console.log('[TableRealtimeSync] Desconectado');
  }
}
